import { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import FiskLogo from "@/components/FiskLogo";
import { toast } from "sonner";

export default function Register() {
  const { user, register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [unit, setUnit] = useState("");
  const [loading, setLoading] = useState(false);

  if (user && user.role) return <Navigate to="/" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("A senha deve ter no mínimo 6 caracteres");
      return;
    }
    if (!unit) {
      toast.error("Selecione sua unidade");
      return;
    }
    setLoading(true);
    const res = await register(name, email, password, unit);
    setLoading(false);
    if (res.ok) {
      toast.success("Cadastro realizado!");
      navigate("/");
    } else {
      toast.error(res.error || "Erro no cadastro");
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <div className="h-16 bg-black flex items-center px-4 sm:px-8">
        <FiskLogo to="/login" />
      </div>

      <div className="flex-1 grid lg:grid-cols-2">
        <div className="hidden lg:flex flex-col justify-between p-12 bg-[#0A0A0A] text-white relative overflow-hidden">
          <div className="absolute inset-0 opacity-10"
               style={{backgroundImage: "radial-gradient(circle at 80% 30%, #E30613 0%, transparent 40%)"}} />
          <div className="relative z-10">
            <p className="text-xs uppercase tracking-[0.3em] text-[#E30613] mb-4">Crie sua conta</p>
            <h1 className="text-4xl xl:text-5xl font-bold leading-tight tracking-tight">
              Comece agora seu<br/>
              <span className="text-[#E30613]">aprendizado</span>.
            </h1>
            <p className="mt-6 text-neutral-400 max-w-md leading-relaxed">
              Cadastro rápido: apenas e-mail e senha. Acesse todos os áudios e vídeos do seu curso em segundos.
            </p>
            <p className="mt-6 text-neutral-300 text-sm font-medium leading-tight">
              FISK Sacomã (SP)<br/>FISK Paulicéia (SBC)
            </p>
          </div>
          <div className="relative z-10 text-xs text-neutral-500">
            © {new Date().getFullYear()} FISK — Unidade Sacomã (SP). Todos os direitos reservados.
          </div>
        </div>

        <div className="flex items-center justify-center p-6 sm:p-12">
          <form onSubmit={onSubmit} className="w-full max-w-md space-y-5" data-testid="register-form">
            <div>
              <h2 className="text-3xl font-bold text-neutral-900">Cadastro</h2>
              <p className="text-sm text-neutral-500 mt-2">É rápido. Em segundos você está estudando.</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="name">Nome completo</Label>
              <Input
                id="name"
                data-testid="register-name-input"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Seu nome"
                className="rounded-sm"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">E-mail</Label>
              <Input
                id="email"
                data-testid="register-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="seu@email.com"
                className="rounded-sm"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Senha (mín. 6 caracteres)</Label>
              <Input
                id="password"
                data-testid="register-password-input"
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="rounded-sm"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="unit">Unidade FISK</Label>
              <Select value={unit} onValueChange={setUnit}>
                <SelectTrigger id="unit" data-testid="register-unit-select" className="rounded-sm">
                  <SelectValue placeholder="Selecione sua unidade" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sacoma" data-testid="register-unit-sacoma">FISK Sacomã (SP)</SelectItem>
                  <SelectItem value="pauliceia" data-testid="register-unit-pauliceia">FISK Paulicéia (SBC)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="register-submit-button"
              className="w-full bg-[#E30613] hover:bg-[#B3040E] text-white rounded-sm h-11 font-semibold"
            >
              {loading ? "Cadastrando..." : "Criar conta"}
            </Button>

            <p className="text-sm text-neutral-600 text-center">
              Já tem cadastro?{" "}
              <Link to="/login" data-testid="go-login-link" className="text-[#E30613] font-medium hover:underline">
                Entrar
              </Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
